import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import createQrCode from 'qrcode-generator';
/**
 * QrCode component.
 *
 * @event renderedToDom Triggered when component has rendered to DOM.
 */

export default class QrCode extends KompisElement {
	// The URL used to generate the QR code.
	@property({ type: String, required: true })
	public url!: string;

	// A title representing the QR code both for accessibility reasons and used as a tooltip, e.g. "QR code to www.ikea.com". The title is used as a tooltip for pointing devices, and can also be read by e.g. screen readers.
	@property({ type: String })
	public qrCodeTitle: string | null = null;

	/**
	 * Renders component.
	 *
	 * @returns HTML.
	 */
	public override render(): RenderResult {
		return html`
			<style>
				svg {
					display: block;
					height: 100%;
					width: 100%;
					print-color-adjust: exact;
					-webkit-print-color-adjust: exact;
				}
			</style>
			${this.getQrCode()}
		`;
	}

	protected override afterRenderToDom(): void {
		this.dispatchEvent(new CustomEvent('renderedToDom', { bubbles: true }));
	}

	private getQrCode(): RenderResult {
		const qr = createQrCode(0, 'M');
		qr.addData(this.url);
		qr.make();

		const svgHtml = qr.createSvgTag({
			margin: 0,
			scalable: true
		});

		const template = document.createElement('template');
		template.innerHTML = svgHtml;
		const svg = template.content.children[0];

		if (this.qrCodeTitle) {
			const titleElement = document.createElementNS('http://www.w3.org/2000/svg', 'title');
			titleElement.textContent = this.qrCodeTitle;
			svg.prepend(titleElement);
			svg.setAttribute('role', 'img');
		}

		return svg;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-qr-code': QrCode;
	}
}
